'use client';

import { cn } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { ChevronDown } from '@shared/icons';
import { NumberFormats as nf, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { Amount, BonusInfoData, BundleStatus } from '../Offer/types';
import { InfoRowsGroup } from './components/InfoRowsGroup';

type BonusInfoSectionProps = {
  bonusInfoData: BonusInfoData;
  bundleStatus?: BundleStatus;
  isOfferDisabled?: boolean;
  namespace: string;
  offerType?: string;
};

function BonusInfoSection({ bonusInfoData, bundleStatus, isOfferDisabled = false, namespace, offerType }: BonusInfoSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const currencyFormatter = useCurrencyFormatter();
  const t = useTranslations(namespace);
  const { showMaxLimits } = getSiteConfig().BONUS_INFO;

  // Early return if no bonus info data (unauthenticated users)
  if (!bonusInfoData) {
    return null;
  }

  if (offerType !== 'BONUS_FUNDS') {
    return null;
  }

  const isExpiredButWasActive = bundleStatus === 'EXPIRED' && bonusInfoData.status === 'ACTIVE';

  const sectionClasses = cn(isOfferDisabled && 'opacity-50', isExpiredButWasActive && 'opacity-80');

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const formatCurrency = (amount: Pick<Amount, 'fixed_amount' | 'currency'>) => {
    return currencyFormatter(amount.fixed_amount).format(nf.WITH_DECIMALS).get();
  };

  const formatAmountOrNA = (amount?: Pick<Amount, 'fixed_amount' | 'currency'>) => {
    return amount && amount?.fixed_amount > 0 ? formatCurrency(amount) : t('bonusInfo.notAvailable');
  };

  const formattedGrantedDate = (() => {
    if (!bonusInfoData.grantedDate) return t('bonusInfo.notAvailable');
    return t('bonusInfo.dateGranted', { date: new Date(bonusInfoData.grantedDate) });
  })();

  const basicInfoRows = [{ labelKey: 'bonusInfo.bonusAwarded', value: formatCurrency(bonusInfoData.initialAmount) }];

  const grantedInfoRows = [
    {
      labelKey: 'bonusInfo.bonusGranted',
      value: formattedGrantedDate,
    },
  ];

  const maxInfoRows = [
    { labelKey: 'bonusInfo.maximumBonusBet', value: formatAmountOrNA(bonusInfoData.maxBet), tooltip: t('bonusInfo.bonusMaxBetTooltip') },
    { labelKey: 'bonusInfo.maximumWin', value: formatAmountOrNA(bonusInfoData.maxWinnings), tooltip: t('bonusInfo.bonusMaxWinTooltip') },
  ];

  return (
    <div className={sectionClasses} data-testid="bonus-info-section">
      <div className="flex justify-start">
        <button
          onClick={handleToggle}
          className="flex items-center justify-between w-full text-foreground hover:text-foreground-hover transition-colors underline underline-offset-4 paragraph-strong"
          data-testid="bonus-info-show-more-button"
        >
          {isExpanded ? t('bonusInfo.showLess') : t('bonusInfo.showMore')}
          <ChevronDown size="extra-small" className={cn('transition-transform duration-200', isExpanded && 'rotate-180')} />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-5 space-y-3">
          <InfoRowsGroup rows={basicInfoRows} namespace={namespace} />

          <div className="border-t border-container-secondary"></div>

          <InfoRowsGroup rows={grantedInfoRows} namespace={namespace} />

          {showMaxLimits && (
            <>
              <div className="border-t border-container-secondary"></div>

              <InfoRowsGroup rows={maxInfoRows} namespace={namespace} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export { BonusInfoSection, type BonusInfoSectionProps };
